import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { loading } from "../components/CustomComponent.js";
import Navbar from "../components/Navbar.js";
import Footer from "../components/Footer.js";
import { Btn, Reveal, Photo } from "../components/ui.js";
import { findSolution, getPlans } from "../data/innovation.js";

/* ============================================================
   LEVELUP ONE — Innovation Detail (/innovation/:slug)
   ============================================================ */

export default function InnovationDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const item = findSolution(slug);

  useEffect(() => {
    loading(false);
    window.scrollTo(0, 0);
  }, [slug]);

  if (!item) {
    return (
      <div className="nx">
        <Navbar />
        <div className="container nx-404">
          <div>
            <div className="nx-404__code nx-gradient-text">ไม่พบโซลูชัน</div>
            <p className="nx-muted mb-4">ไม่พบโซลูชันที่คุณกำลังค้นหา</p>
            <Btn onClick={() => navigate("/innovation")}>กลับไปหน้านวัตกรรม</Btn>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const plans = getPlans(item.slug);

  return (
    <div className="nx">
      <Navbar />

      {/* ===== HERO ===== */}
      <section className="nx-inno-hero" style={{ paddingBottom: 36 }}>
        <span className="nx-orb nx-orb--a" aria-hidden="true" />
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <Reveal>
            <div className="nx-breadcrumb">
              <Link to="/">หน้าหลัก</Link> &nbsp;/&nbsp;
              <Link to="/innovation"> นวัตกรรม</Link> &nbsp;/&nbsp;
              <span> {item.short}</span>
            </div>
            <span className="nx-eyebrow mt-3">{item.badge}</span>
            <h1 className="nx-inno-hero__title mt-3" style={{ fontSize: "clamp(2rem, 5vw, 3.4rem)" }}>{item.title}</h1>
            <p className="nx-primary fw-bold mt-3" style={{ fontSize: "1.05rem" }}>{item.tagline}</p>
            <p className="nx-inno-hero__lead">{item.desc}</p>
            <div className="d-flex flex-wrap gap-3 justify-content-center">
              <Btn to="/booking">ขอใบเสนอราคา</Btn>
              <Btn to="/booking" variant="ghost">นัดเดโมฟรี</Btn>
            </div>
          </Reveal>
        </div>
      </section>

      {/* hero photo */}
      <section className="nx-section--tight" style={{ paddingTop: 0 }}>
        <div className="container">
          <Reveal delay={100}>
            <div style={{ maxWidth: 920, margin: "0 auto", borderRadius: 24, overflow: "hidden", boxShadow: "var(--nx-shadow-lg)" }}>
              <Photo src={item.photo} alt={item.title} fallback="🧩" className="nx-detail-photo" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section className="nx-section">
        <div className="container">
          <div className="row g-5">
            <div className="col-12 col-lg-4">
              <Reveal>
                <span className="nx-eyebrow">FEATURES</span>
                <h2 className="nx-heading mt-3">ทุกสิ่งที่คุณต้องการ<br /><span className="nx-gradient-text">เพื่อความสำเร็จ</span></h2>
                <p className="nx-subtitle">ฟีเจอร์ครบครันที่ออกแบบมาเพื่อให้ธุรกิจของคุณเดินหน้าได้เต็มที่</p>
              </Reveal>
            </div>
            <div className="col-12 col-lg-8">
              {item.features.map((f, i) => (
                <Reveal key={f.title} delay={i * 60}>
                  <div className="d-flex gap-3 py-3" style={{ borderTop: i === 0 ? "none" : "1px solid var(--nx-line)" }}>
                    <span className="nx-card__icon" style={{ marginBottom: 0, flex: "0 0 auto", width: 48, height: 48 }} aria-hidden="true">{f.ico}</span>
                    <div>
                      <h3 className="nx-card__title" style={{ fontSize: "1.1rem", marginBottom: 4 }}>{f.title}</h3>
                      <p className="nx-card__desc">{f.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section className="nx-section nx-bg-surface">
        <div className="container">
          <Reveal>
            <div className="nx-section-head--center">
              <span className="nx-eyebrow">PRICING</span>
              <h2 className="nx-heading mt-3">เลือกแผนที่เหมาะสมกับคุณ</h2>
              <p className="nx-subtitle">ราคาชัดเจน ไม่มีค่าใช้จ่ายแอบแฝง · ทดลองใช้ฟรี 30 วัน</p>
            </div>
          </Reveal>
          <div className="row justify-content-center g-4 mt-2" style={{ maxWidth: 1000, margin: "0 auto" }}>
            {plans.map((p, i) => (
              <div className="col-12 col-md-6 col-lg-4" key={p.name}>
                <Reveal delay={i * 80}>
                  <div className={`nx-pricing ${p.featured ? "nx-pricing--featured" : ""} ${p.custom ? "nx-pricing--custom" : ""}`}>
                    {p.featured && <span className="nx-pricing__badge">POPULAR</span>}
                    <div className="nx-pricing__name">{p.name}</div>
                    {p.custom ? (
                      <div className="nx-pricing__price nx-pricing__price--custom">{p.price}</div>
                    ) : (
                      <div className="nx-pricing__price"><small>฿</small>{p.price}<small> {p.unit}</small></div>
                    )}
                    <div className="nx-pricing__note">{p.note}</div>
                    <ul className="nx-pricing__list">
                      {p.points.map((pt) => <li key={pt}>{pt}</li>)}
                    </ul>
                    <Btn to="/booking" variant={p.featured ? "primary" : "ghost"} block>{p.cta}</Btn>
                  </div>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TAGLINE ===== */}
      <section className="nx-section--tight nx-bg-soft">
        <div className="container">
          <Reveal>
            <div className="nx-tagline">
              <h2 className="nx-tagline__title">สร้างจากไทย — <span className="nx-gradient-text">ส่งมอบทั่วโลก</span></h2>
              <div className="nx-tagline__card">
                <span className="nx-eyebrow">เริ่มโปรเจกต์กับเรา</span>
                <p className="nx-card__desc my-3">คุยฟรี 30 นาที ไม่มีข้อผูกมัด แล้วเราจะช่วยประเมินแนวทางให้</p>
                <Btn to="/booking" block>พูดคุยกับเรา</Btn>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
